// RBI-style Annual Percentage Rate (APR) via reducing-balance IRR.
//
// APR is the annualised effective cost of the loan. Fees are treated as a
// reduction of the amount the borrower actually receives (net disbursal),
// while the EMIs are computed on the gross sanctioned principal. The monthly
// internal rate of return (IRR) that equates the net disbursal to the stream
// of EMIs is then annualised.
//
// Inputs map to lead-table fields:
//   loanAmount     -> principal / sanctioned amount (max credit limit)
//   roi            -> annual reducing-balance interest rate, % (e.g. 36)
//   tenureMonths   -> tenure in months (e.g. 12)
//   processingFee  -> login/processing fee AMOUNT in ₹ (incl. GST if applicable)
//   otherFees      -> any other upfront fee AMOUNT in ₹

// EMI for a reducing-balance loan.
export const calcEmi = (principal, annualRatePct, tenureMonths) => {
  const P = Number(principal);
  const n = Number(tenureMonths);
  const r = Number(annualRatePct) / 12 / 100; // monthly rate
  if (!P || !n) return 0;
  if (r === 0) return P / n;
  const f = Math.pow(1 + r, n);
  return (P * r * f) / (f - 1);
};

// Present value of an annuity of `emi` for `n` periods at monthly rate `i`.
const annuityPV = (emi, n, i) =>
  i === 0 ? emi * n : (emi * (1 - Math.pow(1 + i, -n))) / i;

// Solve for the monthly IRR via bisection (robust, no derivative needed).
const monthlyIrr = (netDisbursed, emi, n) => {
  let lo = 0;
  let hi = 1; // 100%/month — far above any real rate; guarantees a bracket
  for (let k = 0; k < 200; k++) {
    const mid = (lo + hi) / 2;
    const diff = annuityPV(emi, n, mid) - netDisbursed;
    if (Math.abs(diff) < 1e-6) return mid;
    // PV decreases as i increases: if PV still too high, raise the rate.
    if (diff > 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

/**
 * Compute APR (%) from lead-table fields.
 * @returns number APR percentage rounded to 2 dp (0 if inputs invalid).
 */
export const calculateApr = ({
  loanAmount,
  roi,
  tenureMonths,
  processingFee = 0,
  otherFees = 0,
}) => {
  const P = Number(loanAmount);
  const n = Number(tenureMonths);
  if (!P || !n) return 0;

  const emi = calcEmi(P, roi, n);
  const netDisbursed = P - Number(processingFee) - Number(otherFees);
  // No/insufficient net disbursal — cannot compute a meaningful APR.
  if (netDisbursed <= 0 || emi <= 0) return 0;

  const i = monthlyIrr(netDisbursed, emi, n);
  const apr = (Math.pow(1 + i, 12) - 1) * 100;
  return Math.round(apr * 100) / 100;
};

// Convenience: derive the processing-fee AMOUNT from a rate (%) on principal,
// optionally adding GST (e.g. gstPct = 18).
export const feeFromRate = (principal, ratePct, gstPct = 0) => {
  const base = (Number(principal) * Number(ratePct)) / 100;
  return base * (1 + Number(gstPct) / 100);
};

/**
 * Resolve the APR to display on documents (KFS / agreement / sanction letter).
 * The backend-stored value is the source of truth; when it is missing we
 * compute it from the available approval/lead fields so the document is not
 * left blank. numberOfEmi (installment count) is used as the tenure in months.
 * @returns string like "49.41%" (or "" if it cannot be determined).
 */
export const resolveApr = (approval = {}, lead = {}) => {
  const existing = approval.apr ?? lead.apr;
  if (existing !== undefined && existing !== null && existing !== "") {
    return typeof existing === "number" ? `${existing}%` : String(existing);
  }
  const loanAmount = Number(approval.loanAmount ?? lead.loanAmount ?? 0);
  const roi = Number(approval.roi ?? lead.roi ?? 0);
  const tenureMonths = Number(approval.numberOfEmi ?? lead.numberOfEmi ?? 0);
  const feePct = Number(approval.processingFee ?? lead.processingFee ?? 0);
  const processingFee = feeFromRate(loanAmount, feePct);
  const apr = calculateApr({ loanAmount, roi, tenureMonths, processingFee });
  return apr ? `${apr}%` : "";
};

export default calculateApr;
